import { useState } from 'react';
import { Loader2, Search, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { matchingAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const STATUS_STYLES = {
  confirmed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  suggested: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  superseded_by_rerun: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  superseded: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  dismissed: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

function fmtTimestamp(isoString) {
  if (!isoString) return 'never';
  const then = new Date(isoString);
  if (Number.isNaN(then.getTime())) return 'never';
  return then.toLocaleString('en-GB');
}

function Field({ label, value }) {
  return (
    <div><span className="text-muted-foreground">{label}:</span> {value ?? <span className="text-muted-foreground/40">-</span>}</div>
  );
}

// db.accounting_reconciliation record (services/accounting_reconciliation.py)
// is an object, not a scalar -- Field can't render it directly. Collapses it
// to one readable line for this forensic panel. counterpartDisplay is the
// sibling accounting_reconciliation_counterpart_display field (services/
// match_lifecycle_probe.py) -- a resolved invoice_number, preferred over
// the raw counterpart_invoice_id/_external_id below when present; this
// panel still shows the raw truth when no resolution exists (e.g. an
// unpinned/multi-invoice reconciliation), it just prefers readable when it can.
function fmtAccountingReconciliation(ar, counterpartDisplay) {
  if (!ar) return null;
  if (ar.mapping_status === 'unmapped') {
    return `unmapped (${ar.unmapped_reason || 'no reason recorded'})`;
  }
  const reconciled = ar.reconciled ? 'reconciled' : 'not reconciled';
  const counterpartRef = counterpartDisplay
    ? `${counterpartDisplay.source_label}: ${counterpartDisplay.display_ref}`
    : (ar.counterpart_invoice_id || ar.counterpart_invoice_external_id || ar.xero_display_counterpart_invoice_id || ar.xero_display_counterpart_external_id);
  const pinned = ar.counterpart_pinned ? `pinned -> ${counterpartRef}` : (counterpartRef ? `unpinned, counterpart -> ${counterpartRef}` : 'unpinned');
  return `${ar.source_system}, ${ar.match_basis}, ${reconciled}, ${pinned}`;
}

// Formats an arbitrary raw-document field value for the full-fields single-
// match render (2) -- unlike the curated match-documents table below, this
// renders whatever key the backend put on the doc, so it must tolerate any
// JSON shape (array, nested object, bool, null) rather than a hand-picked set.
function fmtFieldValue(key, value) {
  if (value === null || value === undefined) return null;
  if (key.endsWith('_at') || key === 'created_at' || key === 'date') return fmtTimestamp(value);
  if (Array.isArray(value)) return value.length ? value.join(', ') : null;
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

// id first, everything else alphabetical -- same convention as
// matchHistoryColumns below, applied to a single record's own keys.
function sortedFieldKeys(obj) {
  return Object.keys(obj).sort((a, b) => (a === 'id' ? -1 : b === 'id' ? 1 : a.localeCompare(b)));
}

const POINTER_FIELD_LABELS = {
  matched_invoice_id: 'matched_invoice_id (invoice matcher)',
  match_group_id: 'match_group_id (invoice group matcher)',
  matched_statement_id: 'matched_statement_id (statement-payout matcher)',
  matched_recurring_cost_match_id: 'matched_recurring_cost_match_id (recurring-cost matcher)',
};

// Union of keys across every row, so a forensic table shows whatever the
// backend put on each document instead of a hand-picked subset that could
// silently hide a field a matcher started writing later.
function matchHistoryColumns(rows) {
  const cols = new Set();
  rows.forEach((row) => Object.keys(row).forEach((k) => cols.add(k)));
  return Array.from(cols).sort((a, b) => (a === 'id' ? -1 : b === 'id' ? 1 : a.localeCompare(b)));
}

function MatchHistoryTable({ title, rows, emptyMessage, testId }) {
  const columns = matchHistoryColumns(rows);
  return (
    <div className="border rounded-lg p-3 space-y-2" data-testid={testId}>
      <div className="text-sm font-medium">{title} ({rows.length})</div>
      {rows.length === 0 ? (
        <div className="text-xs text-muted-foreground" data-testid={`${testId}-empty`}>{emptyMessage}</div>
      ) : (
        <div className="border rounded overflow-x-auto max-h-72 overflow-y-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-muted/50 sticky top-0">
                {columns.map((col) => (
                  <th key={col} className="text-left px-2 py-1 font-medium whitespace-nowrap">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.id || i} className="border-b last:border-0 hover:bg-muted/50">
                  {columns.map((col) => (
                    <td key={col} className="px-2 py-1 whitespace-nowrap">
                      {col === 'created_at' ? fmtTimestamp(row[col]) : (row[col] ?? '-')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function MatchLifecycleProbePanel() {
  const [query, setQuery] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    const q = query.trim();
    if (!q) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await matchingAPI.matchLifecycleProbe(q);
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const inv = result?.invoice;
  const tx = result?.transaction;
  const pointerInventory = tx?.pointer_inventory;
  // Fall back to the flat scalar so this banner cannot regress against an
  // older backend payload that predates pointer_inventory.
  const anomalyPointerWithoutFlag = pointerInventory
    ? pointerInventory.anomaly_pointer_without_reconciled_flag
    : tx?.anomaly_pointer_without_reconciled_flag;
  const anomalyMultiplePointers = pointerInventory?.anomaly_multiple_live_pointers;
  const statementMatches = result?.statement_payout_matches || [];
  const recurringMatches = result?.recurring_cost_matches || [];
  const blindSpot = inv?.candidate_pool?.sweep_reentry_blind_spot;
  const chain = result?.chain_audit?.latest_chain;
  const chainStillRunning = chain?.status === 'running';

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Match Lifecycle Probe</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Read-only. Enter an invoice number/id, a transaction id, or a match document id
          to see every suggest/confirm/sweep event known for it -- match documents (none
          are ever hard-deleted, newest first), the raw reconciliation-state fields on
          both sides, the BROS re-entry cohort stamp, and the two mechanisms that
          bulk-mutate match status (the Stale Suggested Matches sweep and the auto-match
          chain's own per-run supersede step).
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <Input
            data-testid="match-lifecycle-probe-input"
            placeholder="Invoice number (INV-00359), invoice id, transaction id, or match document id"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') run(); }}
            className="max-w-md"
          />
          <Button data-testid="match-lifecycle-probe-run" onClick={run} disabled={loading || !query.trim()} size="sm">
            {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Probe
          </Button>
        </div>

        {result && (
          <div className="space-y-3" data-testid="match-lifecycle-probe-result">
            {result.match && (
              <div className="border border-emerald-300 rounded-lg p-3 space-y-1 text-xs bg-emerald-50/40 dark:bg-emerald-900/10" data-testid="match-lifecycle-probe-full-match">
                <div className="font-medium text-sm mb-1">Match document {result.match.id} -- every field</div>
                <p className="text-[11px] text-muted-foreground mb-1">
                  Resolved by match document id. Every field on the raw document, not the
                  curated subset the Match documents table below uses.
                </p>
                {sortedFieldKeys(result.match).map((key) => (
                  <Field key={key} label={key} value={fmtFieldValue(key, result.match[key])} />
                ))}
              </div>
            )}
            {anomalyPointerWithoutFlag && (
              <div className="border border-red-300 rounded-lg p-2 flex items-center gap-2 bg-red-50 dark:bg-red-900/10 text-xs" data-testid="match-lifecycle-probe-anomaly">
                <AlertTriangle className="h-4 w-4 text-red-600 shrink-0" />
                Pointer-without-flag anomaly: a matcher pointer field is live but is_reconciled=false.
                No known confirm/revert writer produces this combination -- treat as a genuine
                anomaly, not a UI quirk.
              </div>
            )}
            {anomalyMultiplePointers && (
              <div className="border border-red-300 rounded-lg p-2 flex items-center gap-2 bg-red-50 dark:bg-red-900/10 text-xs" data-testid="match-lifecycle-probe-anomaly-multi-pointer">
                <AlertTriangle className="h-4 w-4 text-red-600 shrink-0" />
                Multiple-live-pointers anomaly: more than one matcher pointer field is live at once
                ({(pointerInventory?.live_pointer_fields || []).join(', ')}) -- two matchers believe
                they own this transaction's reconciliation.
              </div>
            )}
            {blindSpot && (
              <div className="border border-amber-300 rounded-lg p-2 flex items-center gap-2 bg-amber-50 dark:bg-amber-900/10 text-xs" data-testid="match-lifecycle-probe-blindspot">
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                This invoice is BROS-paid-re-entry eligible, but the Stale Suggested
                Matches sweep's own eligibility mirror would flag its suggestion
                "invoice_settled" and supersede it -- the sweep does not know about the
                re-entry $or branch.
              </div>
            )}
            {chainStillRunning && (
              <div className="border border-blue-300 rounded-lg p-2 flex items-center gap-2 bg-blue-50 dark:bg-blue-900/10 text-xs" data-testid="match-lifecycle-probe-chain-wipe">
                <AlertTriangle className="h-4 w-4 text-blue-600 shrink-0" />
                The active auto-match chain is still walking its cohort (run {chain.runs_count},
                cursor {chain.cursor || '(start)'}) -- transactions past the cursor haven't been
                reprocessed yet this run. Suggestions for already-reprocessed transactions are
                live and final the moment they're written (diff-based supersede-on-recompute,
                no more global wipe), but a suggestions-created count read right now still
                reflects only what's been walked so far, not the full cohort.
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-3">
              {inv && (
                <div className="border rounded-lg p-3 space-y-1 text-xs" data-testid="match-lifecycle-probe-invoice">
                  <div className="font-medium text-sm mb-1">Invoice {inv.invoice_number || inv.id}</div>
                  <Field label="matching_status" value={inv.matching_status} />
                  <Field label="payment_status" value={inv.payment_status} />
                  <Field label="review_status" value={inv.review_status} />
                  <Field label="source" value={inv.source} />
                  <Field label="amount" value={inv.amount ? `${inv.amount} ${inv.currency || ''}` : null} />
                  <Field label="paid_at" value={inv.paid_at} />
                  <Field label="bros_reentry_first_seen_at" value={inv.bros_reentry_first_seen_at} />
                  <Field label="findable_in_list_query" value={String(inv.findable_in_list_query)} />
                  <Field label="excluded_as_non_invoice" value={String(inv.excluded_as_non_invoice)} />
                  <Field label="accounting_synced" value={inv.accounting_synced} />
                  {inv.candidate_pool && (
                    <div className="pt-1 border-t mt-1">
                      <Field label="candidate pool (sweep mirror)" value={inv.candidate_pool.sweep_mirror_reason || 'eligible'} />
                      <Field label="bros_paid_reentry_eligible" value={String(inv.candidate_pool.bros_paid_reentry_eligible)} />
                    </div>
                  )}
                  {inv.xero_trace_dedup && (
                    <div className="pt-1 border-t mt-1">
                      <Field label="xero_trace_dedup.is_trace_twin" value={String(inv.xero_trace_dedup.is_trace_twin)} />
                      <div className="text-muted-foreground/70">{inv.xero_trace_dedup.note}</div>
                    </div>
                  )}
                </div>
              )}
              {tx && (
                <div className="border rounded-lg p-3 space-y-1 text-xs" data-testid="match-lifecycle-probe-transaction">
                  <div className="font-medium text-sm mb-1">Transaction {tx.id}</div>
                  <Field label="is_reconciled" value={String(tx.is_reconciled)} />
                  <Field label="matched_invoice_id" value={tx.matched_invoice_id} />
                  <Field label="matched_invoice_ids" value={tx.matched_invoice_ids?.join(', ')} />
                  <Field label="match_group_id" value={tx.match_group_id} />
                  <Field label="amount" value={tx.amount ? `${tx.amount} ${tx.currency || ''}` : null} />
                  <Field label="accounting_synced" value={tx.accounting_synced} />
                  <Field label="accounting_reconciliation" value={fmtAccountingReconciliation(tx.accounting_reconciliation, tx.accounting_reconciliation_counterpart_display)} />
                  <Field
                    label="reconciled_by"
                    value={
                      tx.reconciled_by?.match_missing
                        ? 'match_missing'
                        : tx.reconciled_by?.display_ref
                    }
                  />
                  {pointerInventory && (
                    <div className="pt-1 border-t mt-1" data-testid="match-lifecycle-probe-pointer-inventory">
                      <div className="font-medium text-muted-foreground mb-0.5">pointer_inventory</div>
                      {Object.keys(POINTER_FIELD_LABELS).map((field) => (
                        <Field key={field} label={POINTER_FIELD_LABELS[field]} value={pointerInventory.pointers?.[field]} />
                      ))}
                      <Field label="live_pointer_fields" value={(pointerInventory.live_pointer_fields || []).join(', ') || 'none'} />
                      <Field label="anomaly_pointer_without_reconciled_flag" value={String(pointerInventory.anomaly_pointer_without_reconciled_flag)} />
                      <Field label="anomaly_multiple_live_pointers" value={String(pointerInventory.anomaly_multiple_live_pointers)} />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="border rounded-lg p-3 space-y-2" data-testid="match-lifecycle-probe-matches">
              <div className="text-sm font-medium">
                Match documents (
                {result.matches_truncated
                  ? `showing ${result.matches?.length || 0} of ${result.matches_total_count}`
                  : (result.matches?.length || 0)}
                )
              </div>
              {result.matches_truncated && (
                <div
                  className="border border-amber-300 rounded p-1.5 flex items-center gap-2 bg-amber-50 dark:bg-amber-900/10 text-[11px]"
                  data-testid="match-lifecycle-probe-matches-truncated"
                >
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-600 shrink-0" />
                  Truncated: showing the newest {result.matches?.length || 0} of {result.matches_total_count} match
                  documents -- older rows are not shown. Narrow the query (e.g. probe by match id) to see one directly.
                </div>
              )}
              <p className="text-[11px] text-muted-foreground">
                Newest first. None are ever hard-deleted -- a "swept" or "superseded" match still
                appears here, but the chain wipe and the Stale Suggested Matches sweep both stamp
                superseded_at/superseded_by_job on the document when they flip its status, so
                WHEN and WHICH mechanism is answerable per-document, not just from the aggregate
                job-run timestamps under Sweep / Chain audit below. A blank superseded_at means
                this row's status was last changed by something other than those two mechanisms
                (e.g. a confirm's sibling-dismissal step).
              </p>
              <div className="border rounded overflow-x-auto max-h-72 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/50 sticky top-0">
                      <th className="text-left px-2 py-1 font-medium">Created</th>
                      <th className="text-left px-2 py-1 font-medium">Status</th>
                      <th className="text-left px-2 py-1 font-medium">Confidence</th>
                      <th className="text-left px-2 py-1 font-medium">confirm_step</th>
                      <th className="text-left px-2 py-1 font-medium">dismissal_reason</th>
                      <th className="text-left px-2 py-1 font-medium">superseded_at</th>
                      <th className="text-left px-2 py-1 font-medium">superseded_by_job</th>
                      <th className="text-left px-2 py-1 font-medium">match_group_id</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(result.matches || []).map((m) => (
                      <tr key={m.id} className="border-b last:border-0 hover:bg-muted/50">
                        <td className="px-2 py-1 text-muted-foreground">{fmtTimestamp(m.created_at)}</td>
                        <td className="px-2 py-1">
                          <Badge className={STATUS_STYLES[m.status] || 'bg-slate-100 text-slate-600'}>{m.status}</Badge>
                        </td>
                        <td className="px-2 py-1">{m.confidence ?? '-'}</td>
                        <td className="px-2 py-1">{m.confirm_step ?? '-'}</td>
                        <td className="px-2 py-1">{m.dismissal_reason ?? '-'}</td>
                        <td className="px-2 py-1 text-muted-foreground">{fmtTimestamp(m.superseded_at)}</td>
                        <td className="px-2 py-1">{m.superseded_by_job ?? '-'}</td>
                        <td className="px-2 py-1 font-mono">{m.match_group_id ?? '-'}</td>
                      </tr>
                    ))}
                    {(result.matches || []).length === 0 && (
                      <tr><td colSpan={6} className="px-2 py-2 text-center text-muted-foreground">No match documents found.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <MatchHistoryTable
                title="Statement-payout match history"
                rows={statementMatches}
                emptyMessage="No statement-payout matches have ever existed for this transaction."
                testId="match-lifecycle-probe-statement-history"
              />
              <MatchHistoryTable
                title="Recurring-cost match history"
                rows={recurringMatches}
                emptyMessage="No recurring-cost matches have ever existed for this transaction."
                testId="match-lifecycle-probe-recurring-history"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-3 text-xs">
              <div className="border rounded-lg p-3 space-y-1" data-testid="match-lifecycle-probe-sweep-audit">
                <div className="font-medium">Sweep audit (stale-suggested-matches-sweep)</div>
                {(result.sweep_audit?.recent_runs || []).length === 0 && (
                  <div className="text-muted-foreground">No sweep runs recorded.</div>
                )}
                {(result.sweep_audit?.recent_runs || []).map((r, i) => (
                  <div key={i} className="text-muted-foreground">
                    {fmtTimestamp(r.started_at)} -- swept {r.result?.swept ?? 0} of {r.result?.stale_count ?? 0} stale
                  </div>
                ))}
              </div>
              <div className="border rounded-lg p-3 space-y-1" data-testid="match-lifecycle-probe-chain-audit">
                <div className="font-medium">Auto-match chain audit</div>
                {chain ? (
                  <>
                    <Field label="status" value={chain.status} />
                    <Field label="runs_count" value={chain.runs_count} />
                    <Field label="superseded_count_so_far" value={chain.superseded_count_so_far} />
                    <Field label="matches_created_so_far" value={chain.matches_created_so_far} />
                    <Field label="heartbeat_at" value={fmtTimestamp(chain.heartbeat_at)} />
                  </>
                ) : (
                  <div className="text-muted-foreground">No chain runs recorded.</div>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
